'use client';

import { useState, useEffect } from 'react';
interface PDFViewerProps {
  url: string;
  title?: string;
}
export default function PDFViewer({
  url,
  title
}: PDFViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [blobUrl, setBlobUrl] = useState<string>('');
  useEffect(() => {
    setLoading(true);
    setError(false);

    // URL'yi normalize et - relative path'leri absolute yap
    let normalizedUrl = url;
    if (url.startsWith('/')) {
      // Localhost için HTTP kullan (SSL sorunlarını önler)
      const origin = window.location.origin;
      let baseUrl = origin;

      // Eğer origin HTTPS localhost ise, HTTP'ye çevir
      if (origin.includes('https://localhost') || origin.includes('https://127.0.0.1')) {
        baseUrl = origin.replace('https://', 'http://');
      }
      normalizedUrl = `${baseUrl}${url}`;
    } else if (url.includes('https://localhost') || url.includes('https://127.0.0.1')) {
      // Tam URL'de de localhost HTTPS varsa HTTP'ye çevir
      normalizedUrl = url.replace('https://', 'http://');
    }
    setPdfUrl(normalizedUrl);

    // PDF'i blob olarak yükle (SSL sorunlarını önler)
    const loadPdfAsBlob = async () => {
      try {
        const response = await fetch(normalizedUrl, {
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-cache'
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();

        // PDF tipini kontrol et
        if (!blob.type.includes('pdf') && blob.size === 0) {
          throw new Error('Invalid PDF file');
        }
        const blobUrl_0 = URL.createObjectURL(blob);
        setBlobUrl(blobUrl_0);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        // Hata durumunda direkt URL'yi kullanmayı dene
        setError(false);
        setLoading(false);
        // Blob URL yerine direkt URL kullan
        setBlobUrl('');
      }
    };
    loadPdfAsBlob();
    return () => {
      // Cleanup blob URL will be handled in a separate effect
    };
  }, [url]);

  // Cleanup blob URL when component unmounts or URL changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);
  const handleLoad = () => {
    setLoading(false);
  };
  const handleError = () => {
    setLoading(false);
    setError(true);
  };
  const handleOpenInNewTab = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="relative w-full h-full flex flex-col bg-white dark:bg-[#0F1115]">
        {/* PDF Content */}
        <div className="flex-1 relative overflow-hidden">
          {loading && <div className="absolute inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-900">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400 mb-4"></div>
                <p className="text-slate-600 dark:text-slate-400">Loading PDF...</p>
              </div>
            </div>}
          
          {error ? <div className="absolute inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-900">
              <div className="text-center">
                <p className="text-red-600 dark:text-red-400 mb-4">
                  Failed to load PDF
                </p>
                <button onClick={handleOpenInNewTab} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                  Open in new tab
                </button>
              </div>
            </div> : blobUrl ? <>
              {/* Blob URL kullan - SSL sorunlarını önler */}
              <iframe src={`${blobUrl}#toolbar=1&navpanes=1&scrollbar=1`} className="w-full h-full border-0" onLoad={handleLoad} onError={handleError} title={title || 'PDF Viewer'} />
            </> : pdfUrl ? <>
              {/* Fallback: Direkt URL kullan (HTTP localhost için) */}
              <iframe src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`} className="w-full h-full border-0" onLoad={handleLoad} onError={handleError} title={title || 'PDF Viewer'} />
            </> : null}
        </div>
      </div>
    </div>;
}